import { BehaviorSubject, Observable } from 'rxjs';
import { IChartApi, ISeriesApi, SeriesType, Time } from 'lightweight-charts';
import {
  DrawingManager,
  IDrawing,
  SerializedDrawing,
  DrawingStyle,
  DrawingOptions,
  getToolRegistry,
} from 'lightweight-charts-drawing';
import { getRequiredAnchors, getToolMeta, validateCatalog } from './drawing-tools.catalog';
import { DrawingHistory } from './drawing-history';
import { DrawingStorage } from './drawing-storage';

export interface BarOhlc {
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface DrawingHostContext {
  chart: IChartApi;
  series: ISeriesApi<SeriesType>;
  /** Elemento contenedor del chart (ViewChild del host, no getElementById). */
  container: HTMLElement;
  /** Clave de localStorage (símbolo+timeframe) o null para desactivar persistencia. */
  storageKey: () => string | null;
  /** ms → formato de tiempo de la serie (unix seconds intradía / 'YYYY-MM-DD' diario). */
  formatTime: (ms: number) => Time;
  /** OHLC de la vela en `time`, para el imán. */
  getBarOHLC?: (time: Time) => BarOhlc | null;
  /** Pide texto al usuario para tools de anotación; null cancela la creación. */
  requestText?: (toolType: string) => Promise<string | null>;
  isDark$?: Observable<boolean>;
}

interface ScreenBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

const PREVIEW_ID = '__preview__';
const MAGNET_THRESHOLD_PX = 15;
const TEMPLATE_KEY_PREFIX = 'nectum:drawing-template:v1:';

/**
 * Núcleo reutilizable de drawing tools estilo TradingView sobre
 * lightweight-charts + lightweight-charts-drawing. Una instancia por chart.
 */
export class ChartDrawingController {
  private ctx: DrawingHostContext | null = null;
  private manager: DrawingManager | null = null;
  private registry = getToolRegistry();
  private history = new DrawingHistory();
  private storage = new DrawingStorage();

  // creación interactiva
  private pendingAnchors: Array<{ time: Time; price: number }> = [];
  private freehandDrawing: IDrawing | null = null;
  private idCounter = 0;
  private suppressEvents = false;

  // drag de cuerpo completo (el manager solo arrastra anclas)
  private bodyDrag: {
    drawing: IDrawing;
    startX: number;
    startY: number;
    anchorCoords: Array<{ x: number | null; y: number | null }>;
    originalAnchors: Array<{ time: Time; price: number }>;
    moved: boolean;
  } | null = null;
  private preDragSnapshot: SerializedDrawing | null = null;
  private dragUpdateTimer: ReturnType<typeof setTimeout> | null = null;
  private savedScrollOptions: any = null;
  private mouseDownPoint: { x: number; y: number } | null = null;

  // estilo por defecto (TV usa azul #2962FF)
  public defaultStyle: Partial<DrawingStyle> = { lineColor: '#2962FF', lineWidth: 2 };

  public magnetEnabled = false;
  public stayInDrawingMode = false;

  public readonly activeTool$ = new BehaviorSubject<string | null>(null);
  /** Panel "árbol de objetos" abierto (estado de UI compartido entre toolbar y panel). */
  public readonly objectTreeOpen$ = new BehaviorSubject<boolean>(false);
  public readonly selection$ = new BehaviorSubject<IDrawing | null>(null);
  public readonly selectionScreenBox$ = new BehaviorSubject<ScreenBox | null>(null);
  public readonly drawings$ = new BehaviorSubject<IDrawing[]>([]);
  public get canUndo$(): BehaviorSubject<boolean> { return this.history.canUndo$; }
  public get canRedo$(): BehaviorSubject<boolean> { return this.history.canRedo$; }

  private unsubscribers: Array<() => void> = [];
  private pointerInside = false;

  // ── Ciclo de vida ─────────────────────────────────────────────────────

  public attach(ctx: DrawingHostContext): void {
    this.dispose();
    this.ctx = ctx;
    validateCatalog();

    const manager = new DrawingManager();
    manager.attach(ctx.chart, ctx.series, ctx.container);
    this.manager = manager;

    // eventos del manager
    this.unsubscribers.push(
      manager.on('drawing:selected', e => this.onSelected(e.drawing ?? null)),
      manager.on('drawing:deselected', () => this.onSelected(null)),
      manager.on('drawing:added', () => this.onDrawingsMutated()),
      manager.on('drawing:removed', () => this.onDrawingsMutated()),
      manager.on('drawing:cleared', () => this.onDrawingsMutated()),
      manager.on('drawing:updated', () => this.onDrawingDragged()),
    );

    // clicks del chart (filtra drags de paneo)
    const clickHandler = (params: any) => this.onChartClick(params);
    ctx.chart.subscribeClick(clickHandler);
    this.unsubscribers.push(() => { try { this.ctx?.chart.unsubscribeClick(clickHandler); } catch {} });

    // recolocar la floating toolbar al hacer pan/zoom
    const rangeHandler = () => this.recomputeSelectionBox();
    ctx.chart.timeScale().subscribeVisibleLogicalRangeChange(rangeHandler);
    this.unsubscribers.push(() => {
      try { this.ctx?.chart.timeScale().unsubscribeVisibleLogicalRangeChange(rangeHandler); } catch {}
    });

    // listeners DOM
    this.addDom(ctx.container, 'mousemove', (e: MouseEvent) => this.onMouseMove(e));
    this.addDom(ctx.container, 'mousedown', (e: MouseEvent) => this.onMouseDown(e));
    this.addDom(ctx.container, 'mouseup', (e: MouseEvent) => this.onMouseUp(e));
    this.addDom(ctx.container, 'dblclick', (e: MouseEvent) => this.onDblClick(e));
    this.addDom(ctx.container, 'mouseenter', () => { this.pointerInside = true; });
    this.addDom(ctx.container, 'mouseleave', () => { this.pointerInside = false; });
    this.addDom(document, 'keydown', (e: KeyboardEvent) => this.onKeyDown(e));
  }

  public dispose(): void {
    this.storage.cancelPending();
    if (this.dragUpdateTimer) { clearTimeout(this.dragUpdateTimer); this.dragUpdateTimer = null; }
    for (const off of this.unsubscribers.splice(0)) { try { off(); } catch {} }
    if (this.manager) { try { this.manager.detach(); } catch {} }
    this.manager = null;
    this.ctx = null;
    this.pendingAnchors = [];
    this.freehandDrawing = null;
    this.bodyDrag = null;
    this.history.clear();
    this.activeTool$.next(null);
    this.selection$.next(null);
    this.selectionScreenBox$.next(null);
    this.drawings$.next([]);
  }

  /** Reengancha el manager cuando el host destruye/recrea la serie (p.ej. cambio de tipo de vela). */
  public reattachSeries(series: ISeriesApi<SeriesType>): void {
    if (!this.ctx || !this.manager) return;
    this.ctx.series = series;
    try {
      this.manager.detach();
      this.manager.attach(this.ctx.chart, series, this.ctx.container);
    } catch (e) {
      console.warn('[chart-drawing] reattachSeries falló', e);
    }
  }

  // ── Herramienta activa ────────────────────────────────────────────────

  public setActiveTool(type: string | null): void {
    this.cancelCreation();
    this.activeTool$.next(type);
    try { this.manager?.setActiveTool(type); } catch {}
    if (this.ctx) {
      this.ctx.container.classList.toggle('drawing-cursor', !!type);
    }
    if (type) {
      try { this.manager?.deselectAll(); } catch {}
    }
  }

  public get activeTool(): string | null { return this.activeTool$.value; }

  public toggleMagnet(): void { this.magnetEnabled = !this.magnetEnabled; }
  public toggleStayInDrawingMode(): void { this.stayInDrawingMode = !this.stayInDrawingMode; }

  // ── Creación interactiva ──────────────────────────────────────────────

  private onChartClick(params: any): void {
    const tool = this.activeTool;
    if (!tool || !this.ctx || !this.manager || !params?.point) return;
    const meta = getToolMeta(tool);
    if (meta?.creationMode === 'freehand') return; // se maneja con mousedown/up

    const anchor = this.anchorFromCoords(params.point.x, params.point.y);
    if (!anchor) return;
    this.pendingAnchors.push(anchor);

    if (meta?.creationMode === 'multipoint') {
      // los multipoint acumulan puntos hasta doble click / Enter
      this.updatePreview(anchor);
      return;
    }

    const required = getRequiredAnchors(tool);
    if (this.pendingAnchors.length >= required) {
      const anchors = [...this.pendingAnchors];
      this.pendingAnchors = [];
      this.removePreview();
      void this.finishCreation(tool, anchors);
    } else {
      this.updatePreview(anchor);
    }
  }

  private async finishCreation(tool: string, anchors: Array<{ time: Time; price: number }>): Promise<void> {
    if (!this.manager) return;
    const meta = getToolMeta(tool);
    const options: Record<string, any> = {};

    if (meta?.textField) {
      let text: string | null = meta.textField === 'note' ? 'Nota' : 'Texto';
      if (this.ctx?.requestText) {
        text = await this.ctx.requestText(tool);
        if (text === null) { this.afterCreationEnded(); return; } // cancelado
      }
      options[meta.textField] = text;
    }

    const id = this.nextId();
    const style = { ...this.defaultStyle, ...this.loadTemplate(tool)?.style };
    const templateOptions = this.loadTemplate(tool)?.options;
    const drawing = this.registry.createDrawing(tool, id, anchors as any, style, { ...templateOptions, ...options });
    if (!drawing) { console.warn('[chart-drawing] tipo desconocido:', tool); return; }

    this.manager.addDrawing(drawing);
    this.history.push({ kind: 'add', snapshot: drawing.toJSON() });
    this.scheduleSave();
    this.afterCreationEnded();
    try { this.manager.selectDrawing(id); } catch {}
  }

  private afterCreationEnded(): void {
    this.removePreview();
    this.pendingAnchors = [];
    if (!this.stayInDrawingMode) this.setActiveTool(null);
  }

  private cancelCreation(): void {
    this.pendingAnchors = [];
    this.removePreview();
    if (this.freehandDrawing) {
      try { this.manager?.removeDrawing(this.freehandDrawing.id); } catch {}
      this.freehandDrawing = null;
    }
  }

  // preview: un único drawing actualizado por setAnchors (no se recrea por mousemove)
  private updatePreview(cursorAnchor: { time: Time; price: number }): void {
    const tool = this.activeTool;
    if (!tool || !this.manager || this.pendingAnchors.length === 0) return;
    const required = getRequiredAnchors(tool);
    const anchors = [...this.pendingAnchors];
    while (anchors.length < required) anchors.push(cursorAnchor);

    const preview = this.manager.getDrawing(PREVIEW_ID);
    if (!preview) {
      this.suppressEvents = true;
      try {
        const created = this.registry.createDrawing(tool, PREVIEW_ID, anchors as any, { ...this.defaultStyle });
        if (created) this.manager.addDrawing(created);
      } catch {} finally {
        this.suppressEvents = false;
      }
    } else {
      try {
        preview.setAnchors(anchors as any);
        preview.requestUpdate();
      } catch {}
    }
  }

  private removePreview(): void {
    if (!this.manager) return;
    this.suppressEvents = true;
    try { this.manager.removeDrawing(PREVIEW_ID); } catch {} finally { this.suppressEvents = false; }
  }

  // ── Mouse ─────────────────────────────────────────────────────────────

  private onMouseMove(e: MouseEvent): void {
    if (!this.ctx) return;
    const pt = this.relPoint(e);

    // freehand en curso
    if (this.freehandDrawing) {
      const anchor = this.anchorFromCoords(pt.x, pt.y, false);
      if (anchor) {
        try { (this.freehandDrawing as any).addPoint(anchor); this.freehandDrawing.requestUpdate(); } catch {}
      }
      return;
    }

    // drag de cuerpo completo
    if (this.bodyDrag) {
      this.moveBodyDrag(pt.x, pt.y);
      return;
    }

    // preview de creación
    if (this.activeTool && this.pendingAnchors.length > 0) {
      const anchor = this.anchorFromCoords(pt.x, pt.y);
      if (anchor) this.updatePreview(anchor);
    }
  }

  private onMouseDown(e: MouseEvent): void {
    if (!this.ctx || !this.manager || e.button !== 0) return;
    const pt = this.relPoint(e);
    this.mouseDownPoint = pt;

    const tool = this.activeTool;
    if (tool) {
      const meta = getToolMeta(tool);
      if (meta?.creationMode === 'freehand') {
        const anchor = this.anchorFromCoords(pt.x, pt.y);
        if (!anchor) return;
        const id = this.nextId();
        const style = { ...this.defaultStyle, ...this.loadTemplate(tool)?.style };
        const drawing = this.registry.createDrawing(tool, id, [anchor, anchor] as any, style);
        if (drawing) {
          this.suppressEvents = true;
          try { this.manager.addDrawing(drawing); } finally { this.suppressEvents = false; }
          this.freehandDrawing = drawing;
          this.disableChartPan();
        }
      }
      return;
    }

    // sin tool activo: preparar drag de cuerpo (el manager gestiona el drag de anclas)
    const selected = this.manager.getSelectedDrawing();
    if (selected && !selected.options.locked) {
      const anchorIdx = this.manager.hitTestAnchor(pt);
      if (anchorIdx !== null) {
        // drag de ancla: lo lleva el manager; solo desactivamos el paneo y hacemos snapshot
        this.preDragSnapshot = selected.toJSON();
        this.disableChartPan();
        return;
      }
    }
    const hit = this.manager.hitTest(pt);
    if (hit && !hit.options.locked && hit.id !== PREVIEW_ID) {
      if (this.manager.getSelectedDrawing()?.id !== hit.id) {
        try { this.manager.selectDrawing(hit.id); } catch {}
      }
      this.preDragSnapshot = hit.toJSON();
      this.bodyDrag = {
        drawing: hit,
        startX: pt.x,
        startY: pt.y,
        anchorCoords: hit.anchors.map(a => this.anchorToCoords(a)),
        originalAnchors: hit.anchors.map(a => ({ ...a })),
        moved: false,
      };
      this.disableChartPan();
    }
  }

  private onMouseUp(e: MouseEvent): void {
    if (this.freehandDrawing) {
      const drawing = this.freehandDrawing;
      this.freehandDrawing = null;
      this.enableChartPan();
      this.history.push({ kind: 'add', snapshot: drawing.toJSON() });
      this.onDrawingsMutated();
      this.scheduleSave();
      if (!this.stayInDrawingMode) this.setActiveTool(null);
      return;
    }

    if (this.bodyDrag) {
      const { drawing, moved } = this.bodyDrag;
      this.bodyDrag = null;
      this.enableChartPan();
      if (moved && this.preDragSnapshot) {
        this.history.push({ kind: 'update', before: this.preDragSnapshot, after: drawing.toJSON() });
        this.scheduleSave();
        this.recomputeSelectionBox();
      }
      this.preDragSnapshot = null;
      return;
    }

    // fin de drag de ancla llevado por el manager
    if (this.preDragSnapshot && this.manager) {
      const selected = this.manager.getSelectedDrawing();
      if (selected && selected.id === this.preDragSnapshot.id) {
        const after = selected.toJSON();
        if (JSON.stringify(after.anchors) !== JSON.stringify(this.preDragSnapshot.anchors)) {
          this.history.push({ kind: 'update', before: this.preDragSnapshot, after });
          this.scheduleSave();
          this.recomputeSelectionBox();
        }
      }
      this.preDragSnapshot = null;
      this.enableChartPan();
    }
  }

  private onDblClick(e: MouseEvent): void {
    if (!this.manager) return;
    const tool = this.activeTool;
    const meta = tool ? getToolMeta(tool) : undefined;

    // multipoint: doble click finaliza (los 2 clicks previos del dblclick duplican el último punto)
    if (tool && meta?.creationMode === 'multipoint' && this.pendingAnchors.length >= 2) {
      const anchors = this.pendingAnchors.filter((a, i, arr) =>
        i === 0 || a.time !== arr[i - 1].time || a.price !== arr[i - 1].price
      );
      this.pendingAnchors = [];
      this.removePreview();
      if (anchors.length >= 2) void this.finishCreation(tool, anchors);
      return;
    }

    // doble click sobre un dibujo con texto: editarlo
    if (!tool && this.ctx?.requestText) {
      const pt = this.relPoint(e);
      const hit = this.manager.hitTest(pt);
      const hitMeta = hit ? getToolMeta(hit.type) : undefined;
      if (hit && hitMeta?.textField) {
        void this.ctx.requestText(hit.type).then(text => {
          if (text !== null && hit) {
            const before = hit.toJSON();
            hit.updateOptions({ [hitMeta.textField as string]: text } as any);
            hit.requestUpdate();
            this.history.push({ kind: 'update', before, after: hit.toJSON() });
            this.scheduleSave();
          }
        });
      }
    }
  }

  private moveBodyDrag(x: number, y: number): void {
    if (!this.bodyDrag || !this.ctx) return;
    const { drawing, startX, startY, anchorCoords, originalAnchors } = this.bodyDrag;
    const dx = x - startX;
    const dy = y - startY;
    if (!this.bodyDrag.moved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
    this.bodyDrag.moved = true;

    const timeScale = this.ctx.chart.timeScale();
    const newAnchors = originalAnchors.map((orig, i) => {
      const coord = anchorCoords[i];
      if (coord.x == null || coord.y == null) return orig;
      const time = timeScale.coordinateToTime(coord.x + dx);
      const price = this.ctx!.series.coordinateToPrice(coord.y + dy);
      return {
        time: time ?? orig.time,
        price: price ?? orig.price,
      };
    });
    try {
      drawing.setAnchors(newAnchors as any);
      drawing.requestUpdate();
    } catch {}
    this.recomputeSelectionBox();
  }

  // ── Teclado ───────────────────────────────────────────────────────────

  private onKeyDown(e: KeyboardEvent): void {
    if (!this.manager) return;
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return;

    if (e.key === 'Escape') {
      if (this.activeTool || this.pendingAnchors.length > 0) {
        this.setActiveTool(null);
        e.preventDefault();
      } else if (this.selection$.value) {
        try { this.manager.deselectAll(); } catch {}
      }
      return;
    }

    // el resto de atajos solo con el puntero sobre el chart o con selección
    if (!this.pointerInside && !this.selection$.value) return;

    if ((e.key === 'Delete' || e.key === 'Backspace') && this.selection$.value) {
      this.deleteSelected();
      e.preventDefault();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
      this.undo();
      e.preventDefault();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))) {
      this.redo();
      e.preventDefault();
      return;
    }
    if (this.activeTool && e.key === 'Enter') {
      const meta = getToolMeta(this.activeTool);
      if (meta?.creationMode === 'multipoint' && this.pendingAnchors.length >= 2) {
        const tool = this.activeTool;
        const anchors = [...this.pendingAnchors];
        this.pendingAnchors = [];
        this.removePreview();
        void this.finishCreation(tool, anchors);
        e.preventDefault();
      }
    }
  }

  // ── Edición del dibujo seleccionado ───────────────────────────────────

  public updateStyle(id: string, patch: Partial<DrawingStyle>, recordHistory = true): void {
    const drawing = this.manager?.getDrawing(id);
    if (!drawing) return;
    const before = drawing.toJSON();
    drawing.updateStyle(patch);
    drawing.requestUpdate();
    if (recordHistory) this.history.push({ kind: 'update', before, after: drawing.toJSON() });
    this.scheduleSave();
  }

  public updateOptions(id: string, patch: Partial<DrawingOptions> & Record<string, any>, recordHistory = true): void {
    const drawing = this.manager?.getDrawing(id);
    if (!drawing) return;
    const before = drawing.toJSON();
    drawing.updateOptions(patch);
    drawing.requestUpdate();
    if (recordHistory) this.history.push({ kind: 'update', before, after: drawing.toJSON() });
    this.scheduleSave();
  }

  public setAnchorsOf(id: string, anchors: Array<{ time: Time; price: number }>): void {
    const drawing = this.manager?.getDrawing(id);
    if (!drawing) return;
    const before = drawing.toJSON();
    drawing.setAnchors(anchors as any);
    drawing.requestUpdate();
    this.history.push({ kind: 'update', before, after: drawing.toJSON() });
    this.scheduleSave();
    this.recomputeSelectionBox();
  }

  public getById(id: string): IDrawing | undefined {
    return this.manager?.getDrawing(id);
  }

  /** Cierra una edición en vivo del diálogo de settings como un único comando de history. */
  public commitEdit(id: string, before: SerializedDrawing): void {
    const drawing = this.manager?.getDrawing(id);
    if (!drawing) return;
    const after = drawing.toJSON();
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      this.history.push({ kind: 'update', before, after });
    }
    this.scheduleSave();
    this.recomputeSelectionBox();
  }

  /** Aplica un snapshot completo (para "Cancelar" del diálogo de settings). */
  public applySnapshot(id: string, snapshot: SerializedDrawing): void {
    const drawing = this.manager?.getDrawing(id);
    if (!drawing) return;
    try {
      drawing.fromJSON(snapshot);
      drawing.requestUpdate();
    } catch (e) { console.warn('[chart-drawing] applySnapshot falló', e); }
    this.scheduleSave();
  }

  public cloneSelected(): void {
    const selected = this.selection$.value;
    if (!selected || !this.manager) return;
    const id = this.nextId();
    try {
      const copy = selected.clone(id);
      // pequeño desplazamiento de precio para que no quede exactamente encima
      const prices = copy.anchors.map(a => a.price);
      const offset = (Math.max(...prices) - Math.min(...prices) || Math.abs(prices[0]) * 0.01 || 1) * 0.15;
      copy.setAnchors(copy.anchors.map(a => ({ ...a, price: a.price - offset })) as any);
      this.manager.addDrawing(copy);
      this.history.push({ kind: 'add', snapshot: copy.toJSON() });
      this.scheduleSave();
      this.manager.selectDrawing(id);
    } catch (e) { console.warn('[chart-drawing] clone falló', e); }
  }

  public deleteSelected(): void {
    const selected = this.selection$.value;
    if (!selected || !this.manager) return;
    const snapshot = selected.toJSON();
    try { this.manager.removeDrawing(selected.id); } catch {}
    this.history.push({ kind: 'remove', snapshot });
    this.scheduleSave();
  }

  public removeById(id: string): void {
    const drawing = this.manager?.getDrawing(id);
    if (!drawing) return;
    const snapshot = drawing.toJSON();
    try { this.manager!.removeDrawing(id); } catch {}
    this.history.push({ kind: 'remove', snapshot });
    this.scheduleSave();
  }

  public toggleLock(id: string): void {
    const drawing = this.manager?.getDrawing(id);
    if (!drawing) return;
    this.updateOptions(id, { locked: !drawing.options.locked });
    this.drawings$.next(this.allDrawings());
  }

  public toggleVisible(id: string): void {
    const drawing = this.manager?.getDrawing(id);
    if (!drawing) return;
    this.updateOptions(id, { visible: drawing.options.visible === false });
    this.drawings$.next(this.allDrawings());
  }

  public selectById(id: string): void {
    try { this.manager?.selectDrawing(id); } catch {}
  }

  // ── Plantillas de estilo por tipo ─────────────────────────────────────

  public saveTemplate(drawing: IDrawing): void {
    try {
      const data = drawing.toJSON();
      localStorage.setItem(TEMPLATE_KEY_PREFIX + drawing.type, JSON.stringify({ style: data.style, options: this.templateSafeOptions(data.options) }));
    } catch (e) { console.warn('[chart-drawing] saveTemplate falló', e); }
  }

  public loadTemplate(type: string): { style?: Partial<DrawingStyle>; options?: Record<string, any> } | null {
    try {
      const raw = localStorage.getItem(TEMPLATE_KEY_PREFIX + type);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  public clearTemplate(type: string): void {
    try { localStorage.removeItem(TEMPLATE_KEY_PREFIX + type); } catch {}
  }

  private templateSafeOptions(options: any): any {
    // no heredar en la plantilla campos de instancia (texto, visibilidad, lock)
    const { text, note, visible, locked, ...rest } = options ?? {};
    return rest;
  }

  // ── Gestión global ────────────────────────────────────────────────────

  public allDrawings(): IDrawing[] {
    return (this.manager?.getAllDrawings() ?? []).filter(d => d.id !== PREVIEW_ID);
  }

  public hideAll(): void { this.setAllOptions({ visible: false }); }
  public showAll(): void { this.setAllOptions({ visible: true }); }
  public lockAll(): void { this.setAllOptions({ locked: true }); }
  public unlockAll(): void { this.setAllOptions({ locked: false }); }

  private setAllOptions(patch: Partial<DrawingOptions>): void {
    for (const d of this.allDrawings()) {
      d.updateOptions(patch);
      d.requestUpdate();
    }
    this.drawings$.next(this.allDrawings());
    this.scheduleSave();
  }

  public clearAll(): void {
    if (!this.manager) return;
    const snapshots = this.allDrawings().map(d => d.toJSON());
    if (snapshots.length === 0) return;
    try { this.manager.clearAll(); } catch {}
    this.history.push({ kind: 'clear', snapshots });
    this.scheduleSave();
  }

  public exportJson(): string {
    const data = this.allDrawings().map(d => d.toJSON());
    return JSON.stringify(data, null, 2);
  }

  public importJson(json: string): number {
    if (!this.manager) return 0;
    let data: SerializedDrawing[];
    try {
      data = JSON.parse(json);
      if (!Array.isArray(data)) throw new Error('formato inválido');
    } catch (e) {
      console.warn('[chart-drawing] JSON de import inválido', e);
      return 0;
    }
    let count = 0;
    for (const item of data) {
      try {
        const drawing = this.registry.createDrawing(item.type, item.id || this.nextId(), item.anchors, item.style, item.options);
        if (drawing && !this.manager.getDrawing(drawing.id)) {
          this.manager.addDrawing(drawing);
          count++;
        }
      } catch {}
    }
    if (count > 0) this.scheduleSave();
    return count;
  }

  // ── Undo / redo ───────────────────────────────────────────────────────

  public undo(): void {
    const cmd = this.history.popUndo();
    if (!cmd || !this.manager) return;
    this.suppressEvents = true;
    try {
      switch (cmd.kind) {
        case 'add':
          this.manager.removeDrawing(cmd.snapshot.id);
          break;
        case 'remove':
          this.recreate(cmd.snapshot);
          break;
        case 'update':
          this.applyRaw(cmd.before);
          break;
        case 'clear':
          for (const s of cmd.snapshots) this.recreate(s);
          break;
      }
    } finally {
      this.suppressEvents = false;
    }
    this.drawings$.next(this.allDrawings());
    this.recomputeSelectionBox();
    this.scheduleSave();
  }

  public redo(): void {
    const cmd = this.history.popRedo();
    if (!cmd || !this.manager) return;
    this.suppressEvents = true;
    try {
      switch (cmd.kind) {
        case 'add':
          this.recreate(cmd.snapshot);
          break;
        case 'remove':
          this.manager.removeDrawing(cmd.snapshot.id);
          break;
        case 'update':
          this.applyRaw(cmd.after);
          break;
        case 'clear':
          this.manager.clearAll();
          break;
      }
    } finally {
      this.suppressEvents = false;
    }
    this.drawings$.next(this.allDrawings());
    this.recomputeSelectionBox();
    this.scheduleSave();
  }

  private recreate(snapshot: SerializedDrawing): void {
    if (!this.manager || this.manager.getDrawing(snapshot.id)) return;
    const drawing = this.registry.createDrawing(snapshot.type, snapshot.id, snapshot.anchors, snapshot.style, snapshot.options);
    if (drawing) this.manager.addDrawing(drawing);
  }

  private applyRaw(snapshot: SerializedDrawing): void {
    const drawing = this.manager?.getDrawing(snapshot.id);
    if (!drawing) { this.recreate(snapshot); return; }
    try {
      drawing.fromJSON(snapshot);
      drawing.requestUpdate();
    } catch {}
  }

  // ── Persistencia ──────────────────────────────────────────────────────

  public restoreFromStorage(): void {
    if (!this.manager || !this.ctx) return;
    const key = this.ctx.storageKey();
    this.storage.cancelPending();
    this.suppressEvents = true;
    try {
      this.manager.clearAll();
      for (const item of this.storage.load(key)) {
        try {
          const drawing = this.registry.createDrawing(item.type, item.id, item.anchors, item.style, item.options);
          if (drawing) this.manager.addDrawing(drawing);
        } catch {}
      }
    } finally {
      this.suppressEvents = false;
    }
    this.history.clear();
    this.selection$.next(null);
    this.selectionScreenBox$.next(null);
    this.drawings$.next(this.allDrawings());
  }

  private scheduleSave(): void {
    if (!this.ctx) return;
    this.storage.scheduleSave(this.ctx.storageKey(), () => this.allDrawings().map(d => d.toJSON()));
  }

  // ── Eventos del manager ───────────────────────────────────────────────

  private onSelected(drawing: IDrawing | null): void {
    if (drawing?.id === PREVIEW_ID) return;
    this.selection$.next(drawing);
    this.recomputeSelectionBox();
  }

  private onDrawingsMutated(): void {
    if (this.suppressEvents) return;
    this.drawings$.next(this.allDrawings());
    this.scheduleSave();
  }

  /** drawing:updated se emite en cada paso del drag de ancla del manager. */
  private onDrawingDragged(): void {
    if (this.suppressEvents) return;
    this.recomputeSelectionBox();
    if (this.dragUpdateTimer) clearTimeout(this.dragUpdateTimer);
    this.dragUpdateTimer = setTimeout(() => {
      this.dragUpdateTimer = null;
      this.scheduleSave();
    }, 250);
  }

  // ── Geometría / coordenadas ───────────────────────────────────────────

  private relPoint(e: MouseEvent): { x: number; y: number } {
    const rect = this.ctx!.container.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  private anchorFromCoords(x: number, y: number, applyMagnet = true): { time: Time; price: number } | null {
    if (!this.ctx) return null;
    const timeRaw = this.ctx.chart.timeScale().coordinateToTime(x);
    const rawPrice = this.ctx.series.coordinateToPrice(y);
    if (timeRaw == null || rawPrice == null) return null;
    let price: number = rawPrice;
    const time = this.ctx.formatTime(this.timeToMs(timeRaw));

    if (applyMagnet && this.magnetEnabled && this.ctx.getBarOHLC) {
      const bar = this.ctx.getBarOHLC(time);
      if (bar) {
        let best: number | null = null;
        let bestDist = MAGNET_THRESHOLD_PX;
        for (const v of [bar.open, bar.high, bar.low, bar.close]) {
          const coord = this.ctx.series.priceToCoordinate(v);
          if (coord == null) continue;
          const dist = Math.abs(coord - y);
          if (dist < bestDist) { bestDist = dist; best = v; }
        }
        if (best !== null) price = best;
      }
    }
    return { time, price };
  }

  private anchorToCoords(anchor: { time: Time; price: number }): { x: number | null; y: number | null } {
    if (!this.ctx) return { x: null, y: null };
    let x: number | null = null;
    let y: number | null = null;
    try { x = this.ctx.chart.timeScale().timeToCoordinate(anchor.time); } catch {}
    try { y = this.ctx.series.priceToCoordinate(anchor.price); } catch {}
    return { x, y };
  }

  /** Conversión pública para el diálogo de coordenadas. */
  public anchorTimeToMs(t: Time): number {
    return this.timeToMs(t);
  }

  public msToAnchorTime(ms: number): Time {
    return this.ctx ? this.ctx.formatTime(ms) : (Math.round(ms / 1000) as Time);
  }

  /** Time de lightweight-charts (seconds | 'YYYY-MM-DD' | BusinessDay) → ms. */
  private timeToMs(t: any): number {
    if (typeof t === 'number') return t > 1e12 ? t : Math.round(t * 1000);
    if (typeof t === 'string') {
      const v = Date.parse(t);
      return Number.isFinite(v) ? v : Date.now();
    }
    if (t && typeof t === 'object' && 'year' in t) {
      return Date.UTC(t.year, (t.month ?? 1) - 1, t.day ?? 1);
    }
    return Date.now();
  }

  private recomputeSelectionBox(): void {
    const selected = this.selection$.value;
    if (!selected || !this.ctx) {
      this.selectionScreenBox$.next(null);
      return;
    }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let any = false;
    for (const anchor of selected.anchors) {
      const { x, y } = this.anchorToCoords(anchor);
      if (x == null || y == null) continue;
      any = true;
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    }
    this.selectionScreenBox$.next(any ? { left: minX, top: minY, right: maxX, bottom: maxY } : null);
  }

  // ── Chart pan on/off durante drags ────────────────────────────────────

  private disableChartPan(): void {
    if (!this.ctx || this.savedScrollOptions) return;
    try {
      const opts: any = (this.ctx.chart as any).options();
      this.savedScrollOptions = { handleScroll: opts.handleScroll, handleScale: opts.handleScale };
      this.ctx.chart.applyOptions({ handleScroll: false, handleScale: false } as any);
    } catch {}
  }

  private enableChartPan(): void {
    if (!this.ctx || !this.savedScrollOptions) return;
    try {
      this.ctx.chart.applyOptions(this.savedScrollOptions);
    } catch {}
    this.savedScrollOptions = null;
  }

  // ── Utilidades ────────────────────────────────────────────────────────

  private nextId(): string {
    return `d_${++this.idCounter}_${Date.now()}`;
  }

  private addDom(el: HTMLElement | Document, type: string, handler: any): void {
    el.addEventListener(type, handler);
    this.unsubscribers.push(() => el.removeEventListener(type, handler));
  }
}
